#import "src/allocation/graphql/allocation.graphql"
#import "./memberFragment.graphql"
#import "./teamFragment.graphql"

query getTeamDetail($teamId: String!) {
  team: getGroup(groupId: $teamId) {
    ...TeamDetailsFragment
  }
}

query getMissingTeamsForDiff($groupIds: [String]) {
  getGroups(groupIds: $groupIds) {
    directChildTeamCount
    ...TeamDetailsFragment
    ...TeamMemberCountFragment
  }
}

# Diff reconciliation stubs for teams/members that left/joined a parent roster
# between pits. Fetched by id at a pit date (x-pit-date header). Each side
# (groups/people) is skipped when it has no ids to avoid fetching unfiltered lists.
# Overlay fields share the same @include flags as the org-chart roster query —
# callers must pass directory-gated includes (no bands/dailyRate for directory).
query getDiffMembersAndChildTeams(
  $groupIds: [String]
  $memberIds: [String!]
  $skipTeams: Boolean!
  $skipMembers: Boolean!
  $includePositionFields: Boolean = false
  $includeOverlayBands: Boolean = false
  $includeOverlayLocation: Boolean = false
  $includeOverlayEmail: Boolean = false
  $includeOverlayOrgMetrics: Boolean = false
  $includeOverlayTeamDailyRate: Boolean = false
) {
  childTeams: getGroups(groupIds: $groupIds) @skip(if: $skipTeams) {
    id
    name
    kind
    type
    isHidden
    isDeleted
    directParentId
    hierarchyIds
    memberCount
    childTeamCount
    directMemberCount
    directChildTeamCount
    dailyRate @include(if: $includeOverlayTeamDailyRate)
  }
  members: getPeopleByIds(ids: $memberIds, includeDepartedPeople: true)
    @skip(if: $skipMembers) {
    aggregateId
    employeeId
    firstName
    surname
    preferredName
    jobTitle
    avatar
    fte
    isPlaceholder
    isVacancy
    disabled
    company
    bands @include(if: $includeOverlayBands)
    country @include(if: $includeOverlayLocation)
    city @include(if: $includeOverlayLocation)
    state @include(if: $includeOverlayLocation)
    timezone @include(if: $includeOverlayLocation)
    email @include(if: $includeOverlayEmail)
    address @include(if: $includeOverlayLocation)
    span @include(if: $includeOverlayOrgMetrics)
    layers @include(if: $includeOverlayOrgMetrics)
    directWorkforce @include(if: $includeOverlayOrgMetrics)
    filledPositions @include(if: $includePositionFields) {
      id
      occupancyStatus
      person {
        aggregateId
        preferredName
        firstName
        surname
      }
    }
  }
}

query getMemberCountForTeams($teamIds: [String]) {
  teams: getGroups(groupIds: $teamIds) {
    ...TeamMemberCountFragment
  }
}

query listPrincipleSets($experimentalFeaturesEnabled: Boolean = false) {
  principleSets: listPrincipleSets(
    includeExperimental: $experimentalFeaturesEnabled
  ) {
    name
    id
    suitableGroupTypes
  }
}

mutation selectPrincipleSet($teamId: ID!, $principleSetId: ID) {
  team: selectPrincipleSet(groupId: $teamId, principleSetId: $principleSetId) {
    id
    principleSetId
    principles {
      met
      title
      details
    }
  }
}

mutation updateGroupName($groupId: ID!, $name: String!) {
  result: updateGroupName(groupId: $groupId, name: $name) {
    id
    name
    hierarchyIds
    hierarchy
  }
}

mutation updateTeamAttributes($groupId: ID!, $attributes: [AttributesInput]) {
  group: updateGroupAttributes(groupId: $groupId, attributes: $attributes) {
    id
    attributes {
      id
      attributeType
      subtype
      label
      value
    }
  }
}

query getTeamCsvExport($groupId: String!) {
  csvExport: getGroupCsvExport(groupId: $groupId) {
    content
  }
}

query getPrincipleStatus($teamId: String!) {
  team: getGroup(groupId: $teamId) {
    id
    principles {
      met
      title
      details
      signal {
        color
        text
      }
    }
  }
}
